import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import StoryCard from '@/components/StoryCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Search, X, Sparkles, PenLine, Loader2, ArrowLeft } from 'lucide-react';

const PAGE_SIZE = 24;

/**
 * StoriesCatalog — reusable full catalogue page for a given ownership kind.
 *
 * Used by:
 *  - /stories/intimate  (kind="intimate")
 *  - /stories/community (kind="community")
 *
 * Preserves ALL existing behaviours: filters (category/search/sort), infinite
 * scroll pagination, story URLs, Premium locks. Only the "which pool" changes
 * (server-side via `owner_kind` param).
 */
export default function StoriesCatalog({ kind }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stories, setStories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [communityAuthors, setCommunityAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    search: searchParams.get('search') || '',
    sortBy: searchParams.get('sort') || 'recent',
    authorId: searchParams.get('author') || '',
  });

  const isIntimate = kind === 'intimate';
  const title = isIntimate ? 'Contos Intimate' : 'Contos da Comunidade';
  const subtitle = isIntimate
    ? 'Os contos originais da Intimate, escritos pela equipa editorial e publicados exclusivamente na plataforma.'
    : 'Descobre histórias escritas pelos autores da comunidade Intimate. Novas vozes, novos estilos e novas fantasias.';
  const IconTitle = isIntimate ? Sparkles : PenLine;

  useEffect(() => {
    api.get('/categories').then(({ data }) => setCategories(data || [])).catch(() => {});
    if (!isIntimate) {
      api.get('/authors/community?limit=100').then(({ data }) => setCommunityAuthors(data || [])).catch(() => {});
    }
  }, [isIntimate]);

  const buildQuery = useCallback((skip) => {
    const p = new URLSearchParams();
    p.set('owner_kind', kind);
    p.set('limit', String(PAGE_SIZE));
    p.set('skip', String(skip));
    if (filters.category) p.set('category', filters.category);
    if (filters.search) p.set('search', filters.search);
    return p.toString();
  }, [kind, filters.category, filters.search]);

  // Reload from scratch on filter changes.
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setHasMore(true);
      try {
        const { data } = await api.get(`/stories?${buildQuery(0)}`);
        let list = data || [];
        if (filters.sortBy === 'popular') list = [...list].sort((a, b) => b.views - a.views);
        else if (filters.sortBy === 'rating') list = [...list].sort((a, b) => b.rating - a.rating);
        setStories(list);
        setHasMore(list.length >= PAGE_SIZE);
      } catch {
        setStories([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    };
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [buildQuery, filters.sortBy]);

  // Keep URL in sync so users can share filtered results.
  useEffect(() => {
    const next = new URLSearchParams();
    if (filters.category) next.set('category', filters.category);
    if (filters.search) next.set('search', filters.search);
    if (filters.sortBy && filters.sortBy !== 'recent') next.set('sort', filters.sortBy);
    if (filters.authorId) next.set('author', filters.authorId);
    setSearchParams(next, { replace: true });
  }, [filters, setSearchParams]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get(`/stories?${buildQuery(stories.length)}`);
      const list = data || [];
      let merged = [...stories, ...list];
      if (filters.sortBy === 'popular') merged = [...merged].sort((a, b) => b.views - a.views);
      else if (filters.sortBy === 'rating') merged = [...merged].sort((a, b) => b.rating - a.rating);
      setStories(merged);
      setHasMore(list.length >= PAGE_SIZE);
    } catch { /* ignore */ } finally {
      setLoadingMore(false);
    }
  };

  const displayed = useMemo(() => {
    if (isIntimate) return stories;
    return filters.authorId ? stories.filter((s) => s.author_id === filters.authorId) : stories;
  }, [stories, filters.authorId, isIntimate]);

  const selectedAuthor = communityAuthors.find((a) => a.id === filters.authorId);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={title} description={subtitle} type="website" />
      <Navbar />

      <main className="pt-20 md:pt-24 pb-20 md:pb-12 px-4 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/browse')}
            className="mb-4 text-muted-foreground hover:text-foreground"
            data-testid="catalog-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />Voltar
          </Button>

          {/* Header */}
          <div className="mb-6 md:mb-8">
            <h1 className="font-brand text-2xl md:text-4xl font-bold text-foreground mb-2 flex items-center gap-2">
              <IconTitle className={'w-6 h-6 md:w-8 md:h-8 ' + (isIntimate ? 'text-gold' : 'text-ruby')} />
              {title}
              {selectedAuthor && (
                <span className="text-base md:text-lg font-normal text-muted-foreground">
                  · por {selectedAuthor.name}
                </span>
              )}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground max-w-3xl">{subtitle}</p>
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-3 md:gap-4 mb-6 md:mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar contos..."
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                data-testid="catalog-search"
                className="pl-9 md:pl-10 bg-surface border-border h-10 md:h-12 text-sm md:text-base"
              />
              {filters.search && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setFilters((prev) => ({ ...prev, search: '' }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>

            <div className="flex gap-2 md:gap-4 flex-wrap">
              <Select
                value={filters.category}
                onValueChange={(v) => setFilters((prev) => ({ ...prev, category: v === 'all' ? '' : v }))}
              >
                <SelectTrigger className="flex-1 md:w-48 bg-surface border-border h-10 md:h-12 text-sm">
                  <SelectValue placeholder="Categoria" />
                </SelectTrigger>
                <SelectContent className="bg-surface border-border">
                  <SelectItem value="all">Todas</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.slug} value={cat.slug}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.sortBy}
                onValueChange={(v) => setFilters((prev) => ({ ...prev, sortBy: v }))}
              >
                <SelectTrigger className="flex-1 md:w-48 bg-surface border-border h-10 md:h-12 text-sm">
                  <SelectValue placeholder="Ordenar" />
                </SelectTrigger>
                <SelectContent className="bg-surface border-border">
                  <SelectItem value="recent">Recentes</SelectItem>
                  <SelectItem value="popular">Populares</SelectItem>
                  <SelectItem value="rating">Avaliados</SelectItem>
                </SelectContent>
              </Select>

              {!isIntimate && communityAuthors.length > 0 && (
                <Select
                  value={filters.authorId || 'all'}
                  onValueChange={(v) => setFilters((prev) => ({ ...prev, authorId: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger className="flex-1 md:w-60 bg-surface border-border h-10 md:h-12 text-sm">
                    <SelectValue placeholder="Autor" />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-border">
                    <SelectItem value="all">Todos os autores</SelectItem>
                    {communityAuthors.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name} · {a.story_count}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-ruby" />
            </div>
          ) : displayed.length === 0 ? (
            <p className="text-center text-muted-foreground py-16">
              Sem resultados para os filtros escolhidos.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
                {displayed.map((s) => (
                  <StoryCard
                    key={s.id}
                    story={s}
                    showOfficialBadge={isIntimate}
                    showAuthorLink={!isIntimate}
                  />
                ))}
              </div>

              {hasMore && !filters.authorId && (
                <div className="flex justify-center mt-8">
                  <Button
                    onClick={loadMore}
                    disabled={loadingMore}
                    variant="outline"
                    className="border-ruby/40 text-ruby hover:bg-ruby/10 rounded-full px-6 md:px-8"
                    data-testid="catalog-load-more"
                  >
                    {loadingMore ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />A carregar…</>
                    ) : (
                      <>Carregar mais contos</>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
